import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import Autocomplete from '../era/Autocomplete'
import { AutocompleteCreator } from './AutocompleteCreator'


// Variation of Autocomplete with "Select ${label}" placeholder and fullWidth defaults, plus additional handling for creatable/freeSolo mode.
const AltAutocomplete = ({ creatable, ...field }, ref) => {
  const {
    label,
    placeholder,
    options,
    value,
    onChange,
    getOptionLabel: getOptionLabelProp,
    isOptionEqualToValue: isOptionEqualToValueProp,
    ...props
  } = field
  const newPlaceholder = placeholder || (label ? `Select ${label}` : '')
  const [creatorOpen, setCreatorOpen] = useState(false)
  const [createdDescriptions, setCreatedDescriptions] = useState({})

  const rememberCreatedOption = useCallback((option) => {
    if (!option || typeof option !== 'object' || !option.value || !option.description)
      return

    setCreatedDescriptions((prev) => {
      if (prev[option.value] === option.description) {
        return prev
      }

      return {
        ...prev,
        [option.value]: option.description,
      }
    })
  }, [])

  // Default equality test that handles empty string values gracefully
  const isOptionEqualToValue = useCallback((option, val) => {
    // If the value is an empty string or null, consider it a non-match
    if (val === '' || val === null || val === undefined)
      return false

    if (isOptionEqualToValueProp)
      return isOptionEqualToValueProp(option, val)

    if (typeof val === 'string')
      return option?.value === val || option === val

    // Otherwise, perform a standard comparison
    return option === val
  }, [isOptionEqualToValueProp])

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string')
      return createdDescriptions[option] || option

    if (option?.value && createdDescriptions[option.value])
      return createdDescriptions[option.value]

    if (getOptionLabelProp)
      return getOptionLabelProp(option)

    return option?.description || option?.inputValue || option?.value || ''
  }, [createdDescriptions, getOptionLabelProp])

  // Handle change events properly
  const handleChange = useCallback((event, newValue) => {
    rememberCreatedOption(newValue)

    if (onChange)
      onChange(event, newValue)
  }, [onChange, rememberCreatedOption])

  const freeSoloProps = useMemo(() => {
    if (creatable) {
      const addtProps = {
        freeSolo: true,
        selectOnFocus: true,
        clearOnBlur: true,
        handleHomeEndKeys: true,
        onChange: (event, newValue) => {
          if (newValue && newValue.inputValue) {
            // Handle the creation of a new option
            setCreatorOpen(true)
          } else
            handleChange(event, newValue)
        }
      }

      return addtProps
    }
    return {}
  }, [creatable, handleChange])

  return (<>
    <Autocomplete
      fullWidth
      label={label}
      placeholder={newPlaceholder}
      options={options}
      value={value || null} // Pass null instead of '' to Autocomplete for "no value"
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={handleChange}
      {...freeSoloProps}
      {...props}
      ref={ref}
    />

    {creatable && <AutocompleteCreator parent={{ ...field, onChange: handleChange }} {...creatable}
      onClose={() => setCreatorOpen(false)} open={creatorOpen}
    />}
  </>

  )
}

export default forwardRef(AltAutocomplete)
